import { Component, inject, OnInit } from '@angular/core';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { UtilService } from '../../../services/util.service';
import { API, LocationDTO, LocationFullDTO, TrackingTechnique, VisitorTrackerFullDTO } from '../../../services/api/api';
import { SearchSelectOptions } from '../../../services/gui/modal/modal.model';
import { SbSearchSelectModalComponent } from '../../../services/gui/modal/sb-search-select-modal/sb-search-select-modal.component';
import { ErrorService } from '../../../services/error.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { PermissionService } from '../../../services/permission.service';
import { take } from 'rxjs';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-comp-tracker-creation',
  templateUrl: './tracker-creation.component.html',
  styleUrls: ['./tracker-creation.component.scss'],
  standalone: false,
})
export class TrackerCreationComponent implements OnInit, ModalNavigationComponent {
  private readonly stringTransformation = inject(StringTransformationService);
  isInModalContext: boolean;
  identifier: string;
  name: string;
  location: LocationDTO;
  tracker: VisitorTrackerFullDTO;
  editMode: boolean;
  selectedTechnique: TrackingTechnique;
  techniques: TrackingTechnique[];

  constructor(
    public readonly util: UtilService,
    public readonly api: API,
    private readonly errorHandler: ErrorService,
    private readonly modalNavigation: ModalNavigationService,
    public readonly permissionService: PermissionService
  ) {
    this.editMode = false;
    this.location = undefined;
    this.name = null;
    this.identifier = null;
    this.tracker = undefined;
    this.techniques = [
      TrackingTechnique.Unknown,
      TrackingTechnique.BluetoothBeacon,
      TrackingTechnique.CameraImage,
      TrackingTechnique.Counting,
      TrackingTechnique.WLANTracker,
      TrackingTechnique.OpticalBarrier,
      TrackingTechnique.LiDARScanner,
      TrackingTechnique.ProcessData,
      TrackingTechnique.Study,
      TrackingTechnique.ThermalImage,
      TrackingTechnique.Turnstile,
      TrackingTechnique.WLANAccessPoint,
    ];
    this.selectedTechnique = this.techniques[0];
  }

  ngOnInit(): void {
    if (this.modalNavigation.getCurrentNavigation().data) {
      if (!this.modalNavigation.getSharedData().trackerFlags) {
        this.modalNavigation.getSharedData().trackerFlags = {};
      }
      const modalData = this.modalNavigation.getCurrentNavigation().data;
      const trackerFlags = this.modalNavigation.getSharedData().trackerFlags;
      if (modalData?.editMode) {
        this.tracker = modalData.tracker;
        this.identifier = this.tracker ? this.tracker.identifier : null;
        this.name = this.tracker ? this.tracker.name : null;
        this.location = this.tracker ? this.tracker.location : null;
        this.selectedTechnique = this.tracker ? this.tracker.technique : null;
        this.editMode = modalData.editMode;
        trackerFlags.editMode = this.editMode;
        trackerFlags.tracker = this.tracker;
      }
      this.modalNavigation.getCurrentNavigation().data = {};
    }

    if (this.modalNavigation.getSharedData().locations && this.modalNavigation.getSharedData().locations.length >= 1) {
      this.location = this.modalNavigation.getSharedData().locations[0];
    }
    if (this.modalNavigation.getSharedData().trackerFlags) {
      const trackerFlags = this.modalNavigation.getSharedData().trackerFlags;
      this.tracker = trackerFlags.tracker ? trackerFlags.tracker : this.tracker;
      this.name = trackerFlags.name ? trackerFlags.name : this.name;
      this.identifier = trackerFlags.identifier ? trackerFlags.identifier : this.identifier;
      this.selectedTechnique = trackerFlags.technique ? trackerFlags.technique : this.selectedTechnique;
      this.editMode = trackerFlags.editMode ? trackerFlags.editMode : this.editMode;
    } else {
      this.modalNavigation.getSharedData().trackerFlags = {};
    }
  }

  getLocationText() {
    if (this.location) {
      return this.stringTransformation.locationOrCompanyToShortString(this.location);
    }
    return 'n. def.';
  }

  selectLocation() {
    this.modalNavigation.getSharedData().trackerFlags.tracker = this.tracker;
    this.modalNavigation.getSharedData().trackerFlags.identifier = this.identifier;
    this.modalNavigation.getSharedData().trackerFlags.name = this.name;
    this.modalNavigation.getSharedData().trackerFlags.technique = this.selectedTechnique;
    this.modalNavigation.getSharedData().trackerFlags.editMode = this.editMode;

    const opts: SearchSelectOptions<LocationDTO> = {
      attributeType: 'LOCATION',
      datatype: '',
      title: '',
      singlemode: true,
      initial: this.location ? [this.location] : null,
    };
    this.modalNavigation.navigate(SbSearchSelectModalComponent, opts);
  }

  identifierChange($event: string) {
    if ($event?.length > 0) {
      this.identifier = $event;
    } else {
      this.identifier = null;
    }
  }

  nameChange($event: string) {
    if ($event?.length > 0) {
      this.name = $event;
    } else {
      this.name = null;
    }
  }

  ok() {
    if (this.editMode) {
      this.tracker.identifier = this.identifier;
      this.tracker.name = this.name;
      this.tracker.location = this.location as LocationFullDTO;
      this.tracker.technique = this.selectedTechnique;
      this.api
        .dm_visitor_VisitorTracker_editVisitorTracker(this.tracker)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.close();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.tracker = new VisitorTrackerFullDTO();
      this.tracker.identifier = this.identifier;
      this.tracker.name = this.name;
      this.tracker.location = this.location as LocationFullDTO;
      this.tracker.technique = this.selectedTechnique;
      this.api
        .dm_visitor_VisitorTracker_createVisitorTracker(this.tracker)
        .pipe(take(1))
        .subscribe({
          next: (t: VisitorTrackerFullDTO) => {
            this.close(t);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  close(e?: VisitorTrackerFullDTO) {
    this.modalNavigation.result(e);
  }

  cancel() {
    this.close(null);
  }
}
